import { apiCacheKeys } from '@doltech/core/lib/api/api.urls';
import { queryClient } from '@doltech/core/lib/hooks/withReactQueryConfig';
import { IconArrowsEnter } from '@doltech/icons/IconArrowsEnter';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Modal } from '@doltech/ui/lib/figma/DesignSystemV2/Modal/Modal';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { compose } from '@doltech/utils/lib/compose';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useMutation } from 'react-query';

import { useControlledAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/controlled-audio-play-in-background.store';
import { useRouter } from 'next/router';
import { withUserActivityTracking } from '../landing/hocs/withUserActivityTracking';
import { beforeSubmitPracticeTest } from '../rule-engine/BeforeSubmitPracticeTestEvent';
import { beforeSubmitTest } from '../rule-engine/BeforeSubmitTestEvent';
import { useBeforeSubmitPracticeTestEvent } from '../rule-engine/event/BeforeSubmitPracticeTestEventHandler';
import { useBeforeSubmitTestEvent } from '../rule-engine/event/BeforeSubmitTestEventHandler';
import { useDefaultPolicy } from '../rule-engine/policy/DefaultPolicyHandler';
import { submitTest } from './api/do-test';
import { useListeningFullTestModal } from './hooks/useListeningFullTestModal';
import { ListeningTestSubmission } from './layouts/ListeningTestSubmission';
import { ReadingTestSubmission } from './layouts/ReadingTestSubmission';
import { ListeningTestSubmissionMobile } from './mobile/ListeningTestSubmissionMobile';
import { ReadingTestSubmissionMobile } from './mobile/ReadingTestSubmissionMobile';
import { useTestSubmissionStore } from './model/test-submissions.hook';
import { ReadingTestSubmissionTablet } from './tablet/ReadingTestSubmissionTablet';
import { useFullTestAutoSave, useSaveFullTestProgress } from './useFullTestAutoSave';

interface DoTestSubmissionProps {
  onSubmitSuccess: () => void;
  type?: TestTypeEnum;
  trialUserId?: string;
}

const DoTestSubmission = (props: DoTestSubmissionProps) => {
  const { onSubmitSuccess, type = TestTypeEnum.PRACTICE_TEST, trialUserId } = props;
  const store = useTestSubmissionStore();
  const audioStore = useControlledAudioPlayInBackgroundStore();
  const router = useRouter();

  const [, from768, from1280] = useFromScreens([0, 768, 1280]);
  const submitTestMutate = useMutation(submitTest);
  const submitTestEvent = useBeforeSubmitTestEvent(useDefaultPolicy());
  const submitSectionTestEvent = useBeforeSubmitPracticeTestEvent(useDefaultPolicy());
  const { openModalConfirmSubmit, hide } = useListeningFullTestModal();

  // const getViewGuidelineQuery = useCanViewGuideline();

  React.useEffect(() => {
    if (store && store.skill && type === TestTypeEnum.FULL_TEST) {
      store.setStartTourGuideline(false);

      //   getViewGuidelineQuery.mutate(
      //     {
      //       skill: store.skill,
      //       deviceType: isDesktop ? DeviceTypeEnum.DESKTOP : DeviceTypeEnum.MOBILE,
      //     },
      //     {
      //       onSuccess: (res: any) => {
      //         store.setStartTourGuideline(res.data.canViewGuideline);
      //       },
      //       onError: () => {
      //         store.setStartTourGuideline(true);
      //       },
      //     }
      //   );
    }
    /* eslint-disable-next-line */
  }, [store.skill]);

  const { manualSave } = useSaveFullTestProgress(store, trialUserId);
  const {
    id,
    remainTimeInSeconds,
    practiceExtendTimeInSeconds,
    totalQuestions,
    answeredCount,
    prepareDataForSubmit,
    testType,
    currentTestSection,
    skill,
    isReviewing,
    userExperience: {
      isSubmitAfterWarning,
      isClickSubmitWithinRemainTimeAndQuestion,
      isSubmitAnyway,
      isAnswersAllQuestions,
      lastUserAnswers,
      isChangedAfterDoubleCheck,
      isDoubleCheck,
      isClickSubmitOnTimeWithinRemainQuestion,
      isSubmitOnTime,
    },
    setUserExperience,
  } = store;
  useFullTestAutoSave(store, trialUserId);

  const getUserAnswers = React.useCallback(() => {
    const userAnswers = prepareDataForSubmit();
    delete userAnswers.userExperience;
    return userAnswers;
  }, [prepareDataForSubmit]);

  const handleSubmitListeningFullTest = () => {
    openModalConfirmSubmit({
      title: 'Nộp bài',
      description: 'Bạn có chắc chắn muốn nộp bài không?',
      primaryButton: {
        name: 'OK',
        onClick: async () => {
          await submit();
          audioStore?.pause?.();
          onSubmitSuccess?.();
        },
      },
      secondaryButton: {
        name: 'Cancel',
        onClick: hide,
      },
    });
  };

  const submit = () => {
    submitTestMutate.mutate(
      { testId: id, userAnswers: prepareDataForSubmit(), trialUserId },
      {
        onSuccess: onSubmitSuccess,
      }
    );
  };

  const onSubmitWithUserExperience = (data) => {
    updateUserExperience(data);
    submit();
  };

  const updateUserExperience = React.useCallback(
    (data) => {
      setUserExperience({
        isSubmitAfterWarning: data.isSubmitAfterWarning,
        isClickSubmitWithinRemainTimeAndQuestion: data.isClickSubmitWithinRemainTimeAndQuestion,
        isSubmitAnyway: data.isSubmitAnyway,
        isAnswersAllQuestions: data.isAnswersAllQuestions,
        isChangedAfterDoubleCheck: data.isChangedAfterDoubleCheck,
        lastUserAnswers: data.lastUserAnswers,
        isDoubleCheck: data.isDoubleCheck,
        isClickSubmitOnTimeWithinRemainQuestion: data.isClickSubmitOnTimeWithinRemainQuestion,
        isSubmitOnTime: data.isSubmitOnTime,
      });
    },
    [setUserExperience]
  );

  const onQuit = React.useCallback(() => {
    const { modal, render } = Modal.Confirm.create();
    render(
      () => (
        <Typography.Text variant="regular/16">
          {testType === TestTypeEnum.FULL_TEST
            ? 'Bài của bạn sẽ được lưu lại ở mục in-progress. Bạn có muốn thoát không.'
            : 'Bạn có chắc chắn muốn thoát?'}
        </Typography.Text>
      ),
      {
        onOk: () => {
          manualSave();
          queryClient.invalidateQueries([
            apiCacheKeys.publicService.GET_TRENDING_PRACTICE_TESTS_OVERVIEW,
            skill,
          ]);
          queryClient.invalidateQueries('fetchNewlyUpdatedTests');
          router.replace(store.defaultBackUrl);
          modal.destroy();
          audioStore?.pause?.();
        },
        onCancel: () => {
          store.hideModal();
          modal.destroy();
        },
        okText: 'Thoát',
        cancelText: 'Ở lại',
        maskClosable: true,
        className: 'modal-centered',
        icon: <IconArrowsEnter fontSize={36} color={colorsV2.yellow100} />,
        title: store.testType === TestTypeEnum.FULL_TEST ? 'Thoát và lưu lại bài' : 'Thoát',
      }
    );
  }, [manualSave, skill, store, testType, audioStore, router]);

  const onSubmitTestWithEvents = async () => {
    const userAnswers = getUserAnswers();
    const data = {
      remainQuestion: totalQuestions - answeredCount,
      remainTimeInSeconds: Math.round(remainTimeInSeconds),
      isSubmitAfterWarning,
      isClickSubmitWithinRemainTimeAndQuestion,
      isSubmitAnyway,
      isAnswersAllQuestions,
      lastUserAnswers,
      isChangedAfterDoubleCheck,
      isDoubleCheck,
      isClickSubmitOnTimeWithinRemainQuestion,
      isSubmitOnTime,
      userAnswers: JSON.stringify(userAnswers.userSubmitQuestions),
      remainTimeInMinutes: Math.floor(remainTimeInSeconds / 60),
      skill,
      isReviewing,
      onSubmit: (userExperienceData) => onSubmitWithUserExperience(userExperienceData),
      routeTo: onSubmitSuccess,
    };
    submitTestEvent
      .handle(
        {
          beforeSubmitTest,
        },
        data
      )
      .then(() => {
        updateUserExperience(data);
      });
  };

  const onSubmitPracticeTestWithEvents = async () => {
    const data = {
      remainQuestion: totalQuestions - answeredCount,
      remainTimeInSeconds: Math.round(remainTimeInSeconds),
      completeTimeInMinutes:
        currentTestSection?.duration + Math.ceil(practiceExtendTimeInSeconds / 60),
      onSubmit: (userExperienceData) => onSubmitWithUserExperience(userExperienceData),
      routeTo: onSubmitSuccess,
    };
    await submitSectionTestEvent.handle(
      {
        beforeSubmitPracticeTest,
      },
      data
    );
  };

  const onSubmitWithEvents = async () => {
    switch (testType) {
      case TestTypeEnum.FULL_TEST: {
        return skill === 'LISTENING' ? handleSubmitListeningFullTest() : onSubmitTestWithEvents();
      }
      case TestTypeEnum.PRACTICE_TEST: {
        return onSubmitPracticeTestWithEvents();
      }
      default:
        return noop;
    }
  };

  const SubmissionComponent = React.useMemo(() => {
    switch (store.skill) {
      case SkillEnum.READING: {
        if (from1280) {
          return ReadingTestSubmission;
        }
        if (from768) {
          return ReadingTestSubmissionTablet;
        }
        return ReadingTestSubmissionMobile;
      }
      case SkillEnum.LISTENING: {
        if (from1280) {
          return ListeningTestSubmission;
        }
        return ListeningTestSubmissionMobile;
      }
    }
    return () => null;
  }, [from768, from1280, store.skill]);

  return (
    <SubmissionComponent
      onSubmit={submit}
      onSubmitWithEvents={onSubmitWithEvents}
      onSubmitSuccess={onSubmitSuccess}
      onQuit={onQuit}
    />
  );
};

export default compose(withUserActivityTracking({}), observer)(DoTestSubmission);
